<template>
  <Teleport to="body">
    <Transition name="banner-fade">
      <div v-if="shouldShow" :class="['alert-banner', `alert-banner-${type}`]">
        <div class="alert-banner-icon">
          <InfoIcon v-if="type === 'info'" />
          <SuccessIcon v-else-if="type === 'success'" />
          <WarningIcon v-else-if="type === 'warning'" />
          <ErrorIcon v-else-if="type === 'error'" />
        </div>
        <div class="alert-banner-content">
          <h4 class="alert-banner-title">{{ title }}</h4>
          <p class="alert-banner-message">
            <span v-if="message">{{ message }}</span>
            <template v-if="linkUrl && linkText">
              <a
                class="alert-banner-link"
                :class="{ 'alert-banner-link-spaced': message }"
                :href="linkUrl"
              >
                {{ linkText }}
              </a>
            </template>
          </p>
        </div>
        <button v-if="dismissible" class="alert-banner-close" @click="dismiss" aria-label="Close">
          <CloseIcon />
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onMounted, Teleport, Transition } from 'vue'
import InfoIcon from './icons/InfoIcon.vue'
import SuccessIcon from './icons/SuccessIcon.vue'
import WarningIcon from './icons/WarningIcon.vue'
import ErrorIcon from './icons/ErrorIcon.vue'
import CloseIcon from './icons/CloseIcon.vue'

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: v => ['info', 'success', 'warning', 'error'].includes(v),
  },
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
  linkText: {
    type: String,
    default: '',
  },
  linkUrl: {
    type: String,
    default: '',
  },
  dismissible: {
    type: Boolean,
    default: true,
  },
  duration: {
    type: Number,
    default: 3000, // Default 3 seconds, 0 means don't auto-dismiss
  },
})

const emit = defineEmits(['dismiss'])

// Computed property to ensure reactivity
const shouldShow = computed(() => {
  return !!(props.title && props.message)
})

let timeoutId = null

function dismiss() {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  setTimeout(() => {
    emit('dismiss')
  }, 300) // Wait for fade-out animation
}

function startAutoDismiss() {
  // Error type should never auto-dismiss
  if (props.type === 'error') {
    return
  }

  const autoDismissDuration = props.duration > 0 ? props.duration : 3000

  if (autoDismissDuration > 0) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      dismiss()
    }, autoDismissDuration)
  }
}

// Watch for changes to title and message to start auto-dismiss timer
watch(
  () => [props.title, props.message, props.linkText, props.linkUrl],
  (newValues, oldValues) => {
    // Handle undefined values safely
    const newTitle = newValues?.[0]
    const newMessage = newValues?.[1]
    const newLinkText = newValues?.[2]
    const newLinkUrl = newValues?.[3]
    const oldTitle = oldValues?.[0]
    const oldMessage = oldValues?.[1]
    const oldLinkText = oldValues?.[2]
    const oldLinkUrl = oldValues?.[3]

    const hasContent = !!(newTitle && newMessage)
    const hadContent = !!(oldTitle && oldMessage)

    if (hasContent && !hadContent) {
      // Banner is being shown - start auto-dismiss timer
      startAutoDismiss()
    } else if (!hasContent && hadContent) {
      // Banner is being hidden - clear timer
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    } else if (
      hasContent &&
      hadContent &&
      (newTitle !== oldTitle ||
        newMessage !== oldMessage ||
        newLinkText !== oldLinkText ||
        newLinkUrl !== oldLinkUrl)
    ) {
      // Banner content changed, restart auto-dismiss timer
      startAutoDismiss()
    }
  },
  { immediate: true }
)

// Auto-dismiss logic: error type never auto-dismisses, others use duration
onMounted(() => {
  if (props.title && props.message) {
    startAutoDismiss()
  }
})

watch(
  () => props.duration,
  newDuration => {
    // Error type should never auto-dismiss
    if (props.type === 'error') {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      return
    }

    const autoDismissDuration = newDuration > 0 ? newDuration : 3000

    if (autoDismissDuration > 0 && visible.value) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        dismiss()
      }, autoDismissDuration)
    }
  }
)

defineExpose({
  dismiss,
})
</script>

<style scoped>
.alert-banner {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  min-width: 320px;
  max-width: 600px;
  width: calc(100% - 40px);
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 640px) {
  .alert-banner {
    left: 20px;
    right: 20px;
    transform: none;
    width: auto;
  }
}

.alert-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.alert-banner-icon svg {
  width: 20px;
  height: 20px;
}

.alert-banner-content {
  flex: 1;
  min-width: 0;
}

.alert-banner-title {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.alert-banner-message {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

.alert-banner-link {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.alert-banner-link-spaced {
  margin-left: 4px;
}

.alert-banner-link:hover {
  opacity: 0.85;
}

.alert-banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.alert-banner-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.1) !important;
}

.alert-banner-close svg {
  width: 16px;
  height: 16px;
}

/* Info type */
.alert-banner-info {
  background: #dbeafe;
  border: 1px solid #3b82f6;
}

.alert-banner-info .alert-banner-icon {
  color: #3b82f6;
}

.alert-banner-info .alert-banner-title {
  color: #1e40af;
}

.alert-banner-info .alert-banner-message {
  color: #1e3a8a;
}

.alert-banner-info .alert-banner-close {
  color: #3b82f6;
}

/* Success type */
.alert-banner-success {
  background: #dcfce7;
  border: 1px solid #22c55e;
}

.alert-banner-success .alert-banner-icon {
  color: #22c55e;
}

.alert-banner-success .alert-banner-title {
  color: #15803d;
}

.alert-banner-success .alert-banner-message {
  color: #166534;
}

.alert-banner-success .alert-banner-close {
  color: #22c55e;
}

/* Warning type */
.alert-banner-warning {
  background: #fef3c7;
  border: 1px solid #eab308;
}

.alert-banner-warning .alert-banner-icon {
  color: #eab308;
}

.alert-banner-warning .alert-banner-title {
  color: #a16207;
}

.alert-banner-warning .alert-banner-message {
  color: #854d0e;
}

.alert-banner-warning .alert-banner-close {
  color: #eab308;
}

/* Error type */
.alert-banner-error {
  background: #fee2e2;
  border: 1px solid #ef4444;
}

.alert-banner-error .alert-banner-icon {
  color: #ef4444;
}

.alert-banner-error .alert-banner-title {
  color: #991b1b;
}

.alert-banner-error .alert-banner-message {
  color: #7f1d1d;
}

.alert-banner-error .alert-banner-close {
  color: #ef4444;
}

/* Fade transition animations */
.banner-fade-enter-active {
  transition:
    opacity 0.3s ease-out,
    transform 0.3s ease-out;
}

.banner-fade-leave-active {
  transition:
    opacity 0.3s ease-in,
    transform 0.3s ease-in;
}

.banner-fade-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.banner-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

@media (max-width: 640px) {
  .banner-fade-enter-from {
    transform: translateY(20px);
  }

  .banner-fade-leave-to {
    transform: translateY(20px);
  }
}
</style>
